import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import RequestCard from "../components/RequestCard";
import { CATEGORY_IDS, CATEGORY_ICONS } from "../lib/constants";
import { Search } from "lucide-react";
import { useT } from "../i18n/I18nContext";

export default function Browse() {
  const [params, setParams] = useSearchParams();
  const t = useT();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState(params.get("city") || "");

  const activeCat = params.get("cat") || "all";

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/requests", {
      params: { category: activeCat, city: city || undefined, status: "open" },
    })
      .then(({ data }) => { if (!cancelled) setRequests(data); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [activeCat, city]);

  const setCat = (cat) => {
    const np = new URLSearchParams(params);
    if (cat === "all") np.delete("cat"); else np.set("cat", cat);
    setParams(np);
  };

  const filterPills = useMemo(
    () => [
      { id: "all", labelKey: "categories.all", Icon: Search },
      ...CATEGORY_IDS.map((id) => ({ id, labelKey: `categories.${id}.label`, Icon: CATEGORY_ICONS[id] })),
    ],
    []
  );

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-10 md:py-14 anim-fade-up" data-testid="browse-page">
      <div className="text-xs uppercase tracking-[0.22em] text-mabw-secondary font-semibold mb-2">{t("browse.overline")}</div>
      <h1 className="text-3xl md:text-4xl font-semibold" style={{ fontFamily: "Outfit" }}>{t("browse.title")}</h1>
      <p className="text-mabw-soft mt-2">{t("browse.sub")}</p>

      <div className="mt-6 card-mabw p-4 md:p-5 flex flex-col md:flex-row gap-3">
        <div className="flex-1 flex items-center gap-2 px-3">
          <Search className="w-4 h-4 text-mabw-soft" />
          <input
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder={t("browse.city_placeholder")}
            className="bg-transparent w-full py-2 focus:outline-none"
            data-testid="browse-city-input"
          />
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-2" data-testid="browse-filters">
        {filterPills.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            data-testid={`filter-${c.id}`}
            className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border transition-all ${
              activeCat === c.id
                ? "bg-mabw-primary text-white border-mabw-primary"
                : "bg-white text-mabw-text border-mabw hover:bg-stone-50"
            }`}
          >
            <c.Icon className="w-4 h-4" /> {t(c.labelKey)}
          </button>
        ))}
      </div>

      <div className="mt-8">{renderResults(loading, requests, t)}</div>
    </div>
  );
}

function renderResults(loading, requests, t) {
  if (loading) {
    return <div className="text-mabw-soft" data-testid="browse-loading">{t("common.loading")}</div>;
  }
  if (requests.length === 0) {
    return (
      <div className="card-mabw p-10 text-center text-mabw-soft" data-testid="browse-empty">
        {t("browse.empty")}
      </div>
    );
  }
  return (
    <div className="grid md:grid-cols-2 gap-5">
      {requests.map((r) => <RequestCard key={r.id} request={r} />)}
    </div>
  );
}
